import { httpClient } from './httpClient'
import {
  TravelAccountJourney,
  TravelAccountJourneySet,
  TravelAccountJourneyDetailed
} from '@/models/TravelAccountJourney'

const travelAccountJourneysRoute = '/travel-accounts/journeys'
const bookingInfoRoute = '/baskets/:ref/booked-journey-data'
const getTicketsByEmailRoute = '/tickets/by-email'
const basketTicketsRoute = '/baskets/:ref/tickets'

const addUnmodifiedJourney = (
  bundledJourneys: TravelAccountJourneySet[],
  a: TravelAccountJourney,
  b?: TravelAccountJourney
) => bundledJourneys.push({
  BookingRef: a.BookingRef,
  [a?.IsOutbound ? 'Outbound' : 'Inbound']: a,
  [b?.IsOutbound ? 'Outbound' : 'Inbound']: b
})

const addModifiedJourneys = (
  bundledJourneys: TravelAccountJourneySet[],
  journeys: TravelAccountJourney[]
) => journeys.forEach(j => bundledJourneys.push({
  BookingRef: j.BookingRef,
  [j.IsOutbound ? 'Outbound' : 'Inbound']: j
}))

/*
If a booking with multiple tickets is partially modified then it can split into multiple
outward and inward journeys per booking. In this case it is not possible to
associate the correct outward journeys with their inward journeys. This function bundles
outward and inward journeys together but splits them apart in the case described above.

It does this by grouping journeys by booking reference, then checking if there are more
than two journeys associated with it (hence modified and unbundlable) or if the
"is outbound" property matches on the first two journeys (hence modified and unbundlable)
*/
const bundleTickets = (journeys: TravelAccountJourney[]) =>
  Object.values(journeys
    .reduce((map, j) => {
      (map[j.BookingRef] = map[j.BookingRef] || []).push(j)
      return map
    }, {} as { [x: string]: TravelAccountJourney[] })
  ).reduce((bundledJourneys, journeys) => {
    const [a, b, ...rest] = journeys
    if (a.IsOutbound === b?.IsOutbound || rest.length) {
      addModifiedJourneys(bundledJourneys, journeys)
    } else {
      addUnmodifiedJourney(bundledJourneys, a, b)
    }
    return bundledJourneys
  }, [] as TravelAccountJourneySet[])

const getTravelAccountJourneys = (): Promise<TravelAccountJourneySet[]> =>
  httpClient
    .get<{ Journeys: TravelAccountJourney[] }>(travelAccountJourneysRoute)
    .then(response => bundleTickets(response.data.Journeys))

const getBookingInfo = (bookingRef: string): Promise<TravelAccountJourneyDetailed[]> => {
  const route = bookingInfoRoute.replace(':ref', bookingRef)
  return httpClient
    .get(route)
    .then(response => response.data.Journey.JourneyWays)
}
interface GuestBooking {
  BookingRef: string;
  BookingSecret: string;
}

const getTicketsByEmail =
  async (email: string, ticketId: string): Promise<GuestBooking|false> =>
    await httpClient.post<GuestBooking>(getTicketsByEmailRoute,
      { email: email, productCode: ticketId })
      .then((response): GuestBooking|false => response.data.BookingRef
        ? { BookingRef: response.data.BookingRef, BookingSecret: response.data.BookingSecret }
        : false)
      .catch((): false => false)

const getBasketTickets = (bookingRef: string) => {
  const route = basketTicketsRoute.replace(':ref', bookingRef)
  return httpClient
    .get<{ Journey: TravelAccountJourney[] }>(route)
    .then(response => response.data.Journey)
}

export {
  getTravelAccountJourneys,
  getBookingInfo,
  getBasketTickets,
  getTicketsByEmail
}
